
import { isEmpty, isNil, trim, isFunction, isString, sampleSize } from 'lodash';
import { TweenMax, TimelineMax } from 'gsap';
import FilterMixin from './FilterMixin';
import ValidateValueMixin from './ValidateValueMixin';

export default {

  mixins: [FilterMixin, ValidateValueMixin],

  props: {
    value: {
      required: true,
    },
    label: {
      required: true,
      type: String,
    },
    help: {
      required: false,
      type: String,
      default: '',
    },
    contextHelp: {
      required: false,
      type: String,
      default: '',
    },
    name: {
      required: false,
      type: String,
    },
    validate: {
      required: false,
      type: String,
      default: '',
    },
    cols: {
      default: 12,
    },
    hidden: {
      required: false,
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    type: {
      required: false,
      type: String,
      default: 'text',
    },
    inputType: {
      required: false,
      type: String,
      default: null,
    },
    hasWarning: {
      type: Boolean,
      required: false,
      default: false,
    },
    // convertValueToInternalValue: {
    //   type: Function,
    //   required: false,
    //   default: value => value,
    // },
    // convertInternalValueToValue: {
    //   type: Function,
    //   required: false,
    //   default: value => value,
    // },
  },

  data() {
    return {
      hasFocus: false,
      internalValue: this.convertValueToInternalValue(this.value),
      mounted: false,
      inputLoaded: true,
      disabledByConfig: false,
    };
  },

  computed: {
    disabledCombined: function () {
      return this.disabled || this.disabledByConfig;
    },
    isValueEmpty: function () {
      return isNil(this.value) || trim(this.value).length === 0;
    },
    id: function () {
      const id = this.$attrs.id || this.name || sampleSize('abcdefghijklmnopqrstuvwxyz', 8).join('');
      return isEmpty(this._parentHtmlId) ? id : `${this._parentHtmlId}-${id}`;
    },
    _parentHtmlId: function () {
      let it = this.$parent;
      while (!isNil(it)) {
        if (!isEmpty(it.id)) {
          return it.id;
        }
        it = it.$parent;
      }
      return '';
    },
    privacyBlur: function () {
      if (isEmpty(this.name) || !isString(this.name)) return false;

      const name = this.name.toLowerCase();

      return name.includes('street')
        || name.includes('address')
        || name.includes('zip')
        || name.includes('plz')
        || name.includes('city')
        || name.includes('email')
        || name.includes('phone')
        || name.includes('remark')
        || name.includes('comment')
        || name.includes('firstname')
        || name.includes('middlename')
        || name.includes('lastname')
        || name.includes('guestsearch')
        || name.includes('target')
        || name.includes('companyname')
        || name.includes('company')
        || name.includes('traveldocumentnumber')
        || name.includes('issuingauthority')
        || name.includes('issuedate')
        || name.includes('dateofbirth')
        || name.includes('gastsuche');
    },
  },

  created: function () {
    this.$serviceBus.$on('help.input.show', this.showHelp);

    try {
      this.$attrs = this.$attrs || {};
      const path = this.getHmComponentPath();
      this.$attrs['hm-path'] = path;
      this.disabledByConfig = !this.$root.hasRole('ROLE_SYSADMIN') && this.$root.hmDisabledFields.includes(path);
    } catch (ignore) { /* ignore */ }
  },

  beforeDestroy: function () {
    this.$serviceBus.$off('help.input.show', this.showHelp);
  },

  updated: function () {
  },

  mounted: function () {
    this.mounted = true;
  },

  watch: {
    value: function (newVal) {
      this.internalValue = this.convertValueToInternalValue(this.value);
    },
    internalValue: function (newVal) {
      if (this.mounted === true && this.inputLoaded === true && this.disabled !== true) {
        if (this.isValidValue(newVal)) this.$emit('input', this.convertInternalValueToValue(this.internalValue));
        /*
        // creating way too many breadcrumbs for non-lazy field bindings
        try {
          this.$appsignal.addBreadcrumb({
            category: 'UI',
            action: 'Input value changed',
            metadata: {
              id: this.id,
              newValue: this.type === 'password' ? '********** (hidden)' : this.internalValue,
            },
          });
          // eslint-disable-next-line no-empty
        } catch (ignore) { }
         */
      }
    },
  },

  methods: {
    getHmComponentPath() {
      return `${this.getTree(this)
        .filter(it => !isEmpty(it) && it.startsWith('Page'))
        .join('.')}.${this.id || this.name}`;
    },
    getTree: function (self) {
      const tree = [];
      tree.push(self.$options.name);
      if (!isNil(self.$parent) && self.$parent !== self && self.$parent !== self.$root) {
        this.getTree(self.$parent).forEach(it => tree.push(it));
      }
      return tree;
    },
    convertValueToInternalValue: function (newVal) {
      return newVal;
    },
    convertInternalValueToValue: function (newVal) {
      return newVal;
    },
    showHelp: function () {
      if (this.hasFocus && !isEmpty(this.contextHelp)) {
        this.$driver.highlight(
          {
            element: this.$el,
            padding: 5,
            popover: {
              title: this.label,
              description: this.contextHelp,
              position: 'bottom-center',
            },
          },
        );
      }
    },
    glow() {
      const el = this.$el.querySelector('.hm-input > select,input,textarea,.v-select');
      const tl = new TimelineMax({
        onComplete: () => {
          el.style.boxShadow = null;
        },
      });
      tl.add(TweenMax.fromTo(el, 0.25, { boxShadow: '0 0 0 #2da295' }, { boxShadow: '0 0 0.5rem #2da295' }));
      tl.add(TweenMax.fromTo(el, 0.25, { boxShadow: '0 0 0.5rem #2da295' }, { boxShadow: '0 0 0 #2da295' })).delay(0.5);
    },
    hasSlot: function (slotName) {
      return !isNil(this.$slots[slotName]) || !isNil(this.$scopedSlots[slotName]);
    },
    getSlot: function (slotName) {
      return !isNil(this.$slots[slotName]) ? this.$slots[slotName] : this.$scopedSlots[slotName];
    },
    onFocus: function () {
      this.hasFocus = true;
      this.$emit('focus');
      try {
        this.$rollbar.captureEvent('manual', {
          category: 'UI',
          action: 'User focused input',
          metadata: {
            id: this.id,
            value: this.type === 'password' ? '********** (hidden)' : this.internalValue,
            url: window.location.href,
            user: this.$root.username,
            subdomain: this.$store.getters['hotel/hotelSettings'].hotel.tenant.subdomain,
          },
        }, 'info');
        // eslint-disable-next-line no-empty
      } catch (ignore) { }
    },
    onBlur: function () {
      this.hasFocus = false;
      this.$emit('blur');
      if (!isNil(this.$v)) this.$v.$touch();
      if (!isNil(this.errors)) this.errors.touch(this.name || 'value');
      try {
        this.$rollbar.captureEvent('manual', {
          category: 'UI',
          action: 'User blurred input',
          metadata: {
            id: this.id,
            value: this.type === 'password' ? '********** (hidden)' : this.internalValue,
            url: window.location.href,
            user: this.$root.username,
            subdomain: this.$store.getters['hotel/hotelSettings'].hotel.tenant.subdomain,
          },
        }, 'info');
        // eslint-disable-next-line no-empty
      } catch (ignore) { }
    },
    isNil: function (arg) {
      return isNil(arg);
    },
    isValidValue: function (arg) {
      return true;
    },
    onPaste: function (e) {
      // e.preventDefault();
      // let pastedText = '';

      // if (e.clipboardData && e.clipboardData.getData) {
      //   pastedText = e.clipboardData.getData('text/plain');
      // } else if (window.clipboardData && window.clipboardData.getData) { // IE
      //   pastedText = window.clipboardData.getData('Text');
      // }

      // this.internalValue = pastedText.trim();
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/HmInputMixin.js